import { useState, useEffect } from 'react';
import './common.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import config from '../config';
import { sanitizeTextInput } from '../sanitize';

function DragDrop({ onNavigate }) {

  // Form state
  const [formData, setFormData] = useState({
    grade: '',
    difficultyLevel: 'medium',
    maxWordLength: 6,
    context: '',
    numberOfWords: 5,
    language: 'English',
    hints: true
  });

  // Quiz state
  const [words, setWords] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dropBoxes, setDropBoxes] = useState([]);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [showQuiz, setShowQuiz] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [answeredWords, setAnsweredWords] = useState([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);

  const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  // Load voices for speech synthesis
  useEffect(() => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }
  }, []);

  // Initialize drop boxes when word changes
  useEffect(() => {
    if (showQuiz && words.length > 0) {
      const currentWord = words[currentIndex];
      setDropBoxes(new Array(currentWord.word.length).fill(''));
      setFeedback('');
    }
  }, [currentIndex, showQuiz, words]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const startQuiz = async () => {
    if (!formData.context.trim()) {
      setError('Please enter a context for the words');
      return;
    }

    let age = null;
    if (formData.grade !== '') {
      if (isNaN(Number(formData.grade)) || !Number.isInteger(Number(formData.grade))) {
        setError('Please enter a valid age (numbers only)');
        return;
      }
      age = parseInt(formData.grade);
      if (age < 5 || age > 18) {
        setError('Please enter an age between 5 and 18');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(config.endpoints.spellingQuiz, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          age: age !== null ? age : 8,
          difficultyLevel: formData.difficultyLevel,
          context: sanitizeTextInput(formData.context !== '' ? formData.context : 'general'),
          numberOfWords: parseInt(formData.numberOfWords),
          language: formData.language,
          hints: formData.hints
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to generate words: ${errorText}`);
      }

      const data = await response.json();
      setWords(data);
      setShowQuiz(true);
      setCurrentIndex(0);
      setScore(0);
      setAnsweredWords(new Array(data.length).fill(false));
      setDropBoxes(new Array(data[0].word.length).fill(''));
      setFeedback('');
      
      // Start timer
      const now = Date.now();
      setElapsedTime(0);
      const interval = setInterval(() => {
        setElapsedTime(Date.now() - now);
      }, 1000);
      setTimerInterval(interval);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const speakWord = () => {
    if ('speechSynthesis' in window && words[currentIndex]) {
      const currentWord = words[currentIndex];
      const wordToSpeak = currentWord.word;
      
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(wordToSpeak);
      
      // Detect if word contains Tamil characters
      const isTamil = /[\u0B80-\u0BFF]/.test(wordToSpeak);
      
      const voices = window.speechSynthesis.getVoices();
      if (isTamil || currentWord.language === 'Tamil') {
        utterance.lang = 'ta-IN';
        const tamilVoice =
          voices.find(v => (v.lang.startsWith('ta') || v.lang === 'ta-IN') && /female|woman/i.test(v.name)) ||
          voices.find(v => v.lang.startsWith('ta') || v.lang === 'ta-IN');
        if (tamilVoice) utterance.voice = tamilVoice;
      } else {
        utterance.lang = 'en-US';
        const femaleVoice =
          voices.find(v => v.lang.startsWith('en') && /zira|samantha|victoria|karen|moira|fiona|veena|female|woman/i.test(v.name)) ||
          voices.find(v => v.lang.startsWith('en') && v.name.toLowerCase().includes('female'));
        if (femaleVoice) utterance.voice = femaleVoice;
      }

      utterance.rate = 0.8;
      utterance.pitch = 4.0;
      utterance.volume = 1.0;
      
      window.speechSynthesis.speak(utterance);
    }
  };

  const handleDragStart = (e, letter) => {
    e.dataTransfer.setData('letter', letter);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    const letter = e.dataTransfer.getData('letter');
    const currentWord = words[currentIndex].word.toUpperCase();
    const correctLetter = currentWord[index];

    if (letter === correctLetter) {
      const newDropBoxes = [...dropBoxes];
      newDropBoxes[index] = letter;
      setDropBoxes(newDropBoxes);
      setFeedback('');
      
      if (newDropBoxes.every((box, i) => box === currentWord[i])) {
        setFeedback('🎉 Perfect! You spelled it correctly!');
        if (!answeredWords[currentIndex]) {
          setScore(score + 1);
          const newAnswered = [...answeredWords];
          newAnswered[currentIndex] = true;
          setAnsweredWords(newAnswered);
        }
      }
    } else {
      setFeedback('❌ That letter doesn\'t go there. Try again!');
    }
  };

  const clearBox = (index) => {
    const newDropBoxes = [...dropBoxes];
    newDropBoxes[index] = '';
    setDropBoxes(newDropBoxes);
    setFeedback('');
  };

  const helpMe = () => {
    const currentWord = words[currentIndex].word.toUpperCase();
    setDropBoxes(currentWord.split(''));
    setFeedback('Here\'s the correct spelling!');
  };

  const nextWord = () => {
    if (currentIndex < words.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setFeedback('');
    }
  };

  const previousWord = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setFeedback('');
    }
  };

  const stopQuiz = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }

    const durationInSeconds = Math.floor(elapsedTime / 1000);
    const totalWords = words.length;
    const finalScore = ((score / totalWords) * 100).toFixed(2);

    setShowQuiz(false);
    setWords([]);
    setCurrentIndex(0);
    setDropBoxes([]);
    setFeedback('');

    alert(`Quiz completed! Your score: ${finalScore}%. Duration: ${durationInSeconds} seconds.`);
  };
  
  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (showQuiz && words.length > 0) {
    const currentWord = words[currentIndex];
    
    return (
      <div className="spelling-container">
        <Header onNavigate={onNavigate} showHomeButton={true} />
        <div className="quiz-header">
          <h1 className="quiz-title">🎯 Drag & Drop Spelling</h1>
          <div className="score-timer-container">
            <div className="score-display">
              Score: {score} / {words.length}
            </div>
            <div className="timer-display">
              ⏱️ Time: {formatTime(elapsedTime)}
            </div>
          </div>
        </div>

        <div className="quiz-card">
          <div className="word-counter">
            Word {currentIndex + 1} of {words.length}
          </div>

          <button className="speaker-btn" onClick={speakWord} title="Click to hear the word">
            🔊 <span>Speak Word</span>
          </button>

          <div className="dropzone-section">
            <label className="answer-label">Drag letters to spell the word:</label>
            
            <div className="drop-boxes-container">
              {dropBoxes.map((letter, index) => (
                <div
                  key={index}
                  className={`drop-box ${letter ? 'filled' : ''}`}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                  onClick={() => clearBox(index)}
                  title={letter ? 'Click to clear' : 'Drop letter here'}
                >
                  {letter || ''}
                </div>
              ))}
            </div>

            {formData.hints && currentWord.phoneticHint && (
              <div className="hint-box">
                💡 Hint: {currentWord.phoneticHint}
              </div>
            )}

            {feedback && (
              <div className={`feedback ${feedback.includes('Perfect') || feedback.includes('correct') ? 'correct' : 'incorrect'}`}>
                {feedback}
              </div>
            )}
          </div>

          <div className="alphabet-section">
            <label className="alphabet-label">Drag letters from here:</label>
            <div className="alphabet-container">
              {ALPHABET.map((letter, index) => (
                <div
                  key={index}
                  className="letter-tile"
                  draggable
                  onDragStart={(e) => handleDragStart(e, letter)}
                >
                  {letter}
                </div>
              ))}
            </div>
          </div>

          <div className="button-group">
            <button className="help-btn" onClick={helpMe}>
              🆘 Help Me
            </button>
          </div>

          <div className="navigation-buttons">
            <button
              className="nav-btn prev-btn"
              onClick={previousWord}
              disabled={currentIndex === 0}
            >
              ← Previous
            </button>
            <button className="stop-btn" onClick={stopQuiz}>
              ⏹ Stop Quiz
            </button>
            <button
              className="nav-btn next-btn"
              onClick={nextWord}
              disabled={currentIndex === words.length - 1}
            >
              Next →
            </button>
          </div>
        </div>

        <div className="progress-bar">
          {words.map((_, index) => {
            const isActive = index === currentIndex ? ' active' : '';
            const isCompleted = answeredWords[index] ? ' completed' : '';
            return (
              <div
                key={index}
                className={`progress-dot${isActive}${isCompleted}`}
              />
            );
          })}
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="spelling-container">
      <Header onNavigate={onNavigate} showHomeButton={true} />
      <div className="setup-header">
        <h1 className="setup-title">🎨 Drag & Drop Spelling Setup</h1>
        <p className="setup-description">
          Drag letters to spell words - a fun interactive way to learn!
        </p>
      </div>

      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      <div className="setup-form">
        <div className="form-row">
          <div className="form-group">
            <label>Age <span style={{ fontWeight: 'normal', fontSize: '0.9em', color: '#888' }}></span></label>
            <input
              type="text"
              name="grade"
              value={formData.grade}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter age (e.g. 8)"
            />
          </div>

          <div className="form-group">
            <label>Difficulty Level</label>
            <select
              name="difficultyLevel"
              value={formData.difficultyLevel}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Max Word Length</label>
            <input
              type="number"
              name="maxWordLength"
              value={formData.maxWordLength}
              onChange={handleInputChange}
              className="form-input"
              min="3"
              max="15"
            />
          </div>

          <div className="form-group">
            <label>Number of Words</label>
            <input
              type="number"
              name="numberOfWords"
              value={formData.numberOfWords}
              onChange={handleInputChange}
              className="form-input"
              min="1"
              max="20"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Context / Topic</label>
          <input
            type="text"
            name="context"
            value={formData.context}
            onChange={handleInputChange}
            className="form-input"
            placeholder="e.g., animals, sports, food, etc."
          />
        </div>

        <div className="form-row">
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="hints"
                checked={formData.hints}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span>Show Phonetic Hints</span>
            </label>
          </div>
        </div>

        <button
          className="start-btn"
          onClick={startQuiz}
          disabled={loading}
        >
          {loading ? '🔄 Generating Words...' : '🚀 Start Quiz'}
        </button>
      </div>
      <Footer />
    </div>
  );
}

export default DragDrop;